/**
 * Premium — the binding-amount block of Premium Selection.
 *
 * Split out of the old combined "Fees & Premium" card (Andressa, approved
 * 4-ago). Per the design notes, separating the premium from the fees lets the
 * underwriter test a selected premium against the loss-year figures with both
 * in view, and leaves the interface less crowded.
 *
 * Selected Premium is the SLOW recalc trigger (~1 min against the real
 * backend); the derived rows below it show a skeleton until the recalc
 * resolves. Final Premium / Full Loss Ratio / Deviation from Unity repeat the
 * summary-card values inline so the underwriter sees them change without
 * scrolling back up (Figma rationale).
 */
import { Lightbulb02 } from "@untitledui/icons";
import { Select, Tooltip, TooltipTrigger, cx } from "@tricura/atlas-ui";
import { FormGroup } from "../../../../components/form-group";
import { IntInput } from "../../../../components/int-input";
import { MoneyInput } from "../../../../components/money-input";
import type { PremiumSelectionDerived, PremiumSelectionInputs } from "../../../../domain/pricing";
import { CardRows, ComputedRow, InfoTip } from "../components/field-row";
import { useBlurFlash } from "../components/use-blur-flash";
import { clampMoneyToWire, displayMoneyValue, displayPercent } from "../premium-selection-types";
import { FIELD_TOOLTIPS } from "../tooltips";
import type { DerivedKey, InputKey, PendingMap } from "../use-premium-selection";

/** Premium header glyph — Andressa's Figma export ("MVP 1 Updates 07/28"), the
 *  same banknote the combined card used. Stroke uses currentColor so
 *  FormGroup's brand color applies.
 *
 *  The size is overridden rather than inherited: the export is a 32-unit frame
 *  whose drawing occupies 26.67 × 18.67 of it, and Figma specs the drawing at
 *  that size — so the frame has to render at its natural 32px, not at
 *  FormGroup's default 20px, which would shrink the banknote to 16.67 wide.
 *  `!` because a bare `size-8` and the injected `size-5` have equal
 *  specificity, and which one wins would come down to stylesheet order. */
function PremiumIcon({ className }: { className?: string }) {
    return (
        <svg
            viewBox="0 0 32 32"
            fill="none"
            className={cx(className, "!size-8")}
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M18.6665 11.9993H15.3332C14.2286 11.9993 13.3332 12.8948 13.3332 13.9993C13.3332 15.1039 14.2286 15.9993 15.3332 15.9993H16.6665C17.7711 15.9993 18.6665 16.8948 18.6665 17.9993C18.6665 19.1039 17.7711 19.9993 16.6665 19.9993H13.3332M15.9998 10.666V11.9993M15.9998 19.9993V21.3327M23.9998 15.9993H24.0132M7.99984 15.9993H8.01317M2.6665 10.9327L2.6665 21.066C2.6665 22.5595 2.6665 23.3062 2.95715 23.8767C3.21282 24.3784 3.62076 24.7864 4.12253 25.042C4.69296 25.3327 5.4397 25.3327 6.93317 25.3327L25.0665 25.3327C26.56 25.3327 27.3067 25.3327 27.8771 25.042C28.3789 24.7864 28.7869 24.3784 29.0425 23.8767C29.3332 23.3062 29.3332 22.5595 29.3332 21.066V10.9327C29.3332 9.43921 29.3332 8.69247 29.0425 8.12204C28.7869 7.62028 28.3789 7.21233 27.8771 6.95667C27.3067 6.66602 26.56 6.66602 25.0665 6.66602L6.93317 6.66602C5.4397 6.66602 4.69296 6.66602 4.12253 6.95666C3.62076 7.21233 3.21282 7.62027 2.95715 8.12204C2.6665 8.69247 2.6665 9.43921 2.6665 10.9327ZM24.6665 15.9993C24.6665 16.3675 24.368 16.666 23.9998 16.666C23.6316 16.666 23.3332 16.3675 23.3332 15.9993C23.3332 15.6312 23.6316 15.3327 23.9998 15.3327C24.368 15.3327 24.6665 15.6312 24.6665 15.9993ZM8.6665 15.9993C8.6665 16.3675 8.36803 16.666 7.99984 16.666C7.63165 16.666 7.33317 16.3675 7.33317 15.9993C7.33317 15.6312 7.63165 15.3327 7.99984 15.3327C8.36803 15.3327 8.6665 15.6312 8.6665 15.9993Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/** Accelerant's hard limit on the capped 5-year loss ratio (OR-1358). At or
 *  above this the value turns red with a threshold-breach tooltip. Fraction
 *  (cappedLossRatio5yr arrives 0–1). */
const LOSS_RATIO_ALERT_THRESHOLD = 0.4;

/** Probability to Bind options — persisted to submission.probability_to_bind. */
const PROBABILITY_OPTIONS = [
    { id: "Low", label: "Low" },
    { id: "Medium", label: "Medium" },
    { id: "High", label: "High" },
];

interface PremiumCardProps {
    inputs: PremiumSelectionInputs;
    derived: PremiumSelectionDerived | null;
    pending: PendingMap;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
    /** Probability to Bind (submission field) — lives in this card per the
     *  latest Figma, persisted independently of the quote. */
    probabilityToBind: string;
    onProbabilityToBindChange: (value: string) => void;
}

export function PremiumCard({
    inputs,
    derived,
    pending,
    setInput,
    commit,
    probabilityToBind,
    onProbabilityToBindChange,
}: PremiumCardProps) {
    const isPending = (key: DerivedKey) => Boolean(pending[key]) || derived === null;

    return (
        <FormGroup title="Premium" icon={PremiumIcon} data-autotest="premium-selection-premium-card">
            <CardRows>
                <SelectedPremiumRow
                    value={inputs.selectedPremium}
                    suggested={derived?.premiumAtTargetLossRatio ?? null}
                    setInput={setInput}
                    commit={commit}
                />
                <ComputedRow
                    label="Final Premium"
                    tooltip={FIELD_TOOLTIPS.finalPremium}
                    value={displayMoneyValue(derived?.finalPremium ?? null)}
                    isPending={isPending("finalPremium")}
                    data-autotest="premium-selection-premium-final-premium-row"
                />
                {/* Loss-ratio block (backend PR #303 + Andressa's design): the
                  * underwriter picks the utilized-years window; the backend
                  * recomputes both ratios on save. No tooltip on the input row —
                  * the design shows the ⓘ only on the two ratio rows. */}
                <div className="flex min-h-15 items-center justify-between gap-4 py-3" data-autotest="premium-selection-premium-utilized-loss-years-row">
                    <span className="text-secondary text-sm font-medium">Utilized Loss Years</span>
                    <div className="w-24 shrink-0">
                        <IntInput
                            aria-label="Utilized Loss Years"
                            value={inputs.utilizedLossYears}
                            onChange={(raw) => setInput("utilizedLossYears", raw)}
                            onBlur={() => commit("utilizedLossYears")}
                        />
                    </div>
                </div>
                <ComputedRow
                    label="Full Loss Ratio"
                    tooltip={FIELD_TOOLTIPS.fullLossRatio}
                    value={displayPercent(derived?.fullLossRatio ?? null)}
                    isPending={isPending("fullLossRatio")}
                    data-autotest="premium-selection-premium-full-loss-ratio-row"
                />
                <ComputedRow
                    label="Capped 5 Year Loss Ratio"
                    tooltip={FIELD_TOOLTIPS.cappedLossRatio5yr}
                    value={displayPercent(derived?.cappedLossRatio5yr ?? null)}
                    isPending={isPending("cappedLossRatio5yr")}
                    alert={(derived?.cappedLossRatio5yr ?? 0) >= LOSS_RATIO_ALERT_THRESHOLD}
                    alertTooltip="Exceeds the 40% target"
                    data-autotest="premium-selection-premium-capped-5-year-loss-ratio-row"
                />
                <ComputedRow
                    label="Deviation from Unity"
                    tooltip={FIELD_TOOLTIPS.deviationFromUnity}
                    value={displayPercent(derived?.deviationFromUnity ?? null)}
                    isPending={isPending("deviationFromUnity")}
                    data-autotest="premium-selection-premium-deviation-from-unity-row"
                />
                <ProbabilityRow value={probabilityToBind} onChange={onProbabilityToBindChange} />
            </CardRows>
        </FormGroup>
    );
}

/** Row holding the Probability to Bind dropdown. */
function ProbabilityRow({ value, onChange }: { value: string; onChange: (value: string) => void }) {
    return (
        <div
            className="flex min-h-15 items-center justify-between gap-4 py-3"
            data-autotest="premium-selection-premium-probability-to-bind-row"
        >
            <span className="text-secondary inline-flex items-center gap-1.5 text-sm font-medium">
                Probability to Bind
                <InfoTip tooltip={FIELD_TOOLTIPS.probabilityToBind} />
            </span>
            <div className="w-32 shrink-0">
                <Select
                    size="sm"
                    aria-label="Probability to Bind"
                    placeholder="Select"
                    selectedKey={value || null}
                    items={PROBABILITY_OPTIONS}
                    onSelectionChange={(key) => key !== null && onChange(String(key))}
                >
                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                </Select>
            </div>
        </div>
    );
}

/** Selected Premium row — like DollarRow but with a "Suggested Premium"
 *  chip next to the label (Andressa, jul-22). The chip shows the system's
 *  premium-at-target-loss-ratio; clicking it populates the input. */
function SelectedPremiumRow({
    value,
    suggested,
    setInput,
    commit,
}: {
    value: string;
    /** Premium at target loss ratio (backend). Null → no chip. */
    suggested: number | null;
    setInput: (key: InputKey, value: string) => void;
    commit: (key: InputKey) => void;
}) {
    const { flashKey, flash } = useBlurFlash();
    return (
        <div className="flex min-h-15 items-center justify-between gap-4 py-3" data-autotest="premium-selection-premium-selected-premium-row">
            <span className="text-secondary inline-flex items-center gap-2 text-sm font-medium">
                Selected Premium
                {suggested !== null && (
                    <SuggestedPremiumChip
                        amount={suggested}
                        onClick={() => {
                            setInput("selectedPremium", String(suggested));
                            commit("selectedPremium");
                            flash();
                        }}
                    />
                )}
            </span>
            <div key={flashKey} className={`w-40 shrink-0 rounded-lg${flashKey > 0 ? " field-flash" : ""}`}>
                <MoneyInput
                    aria-label="Selected Premium"
                    value={value}
                    onChange={(raw) => setInput("selectedPremium", raw)}
                    onBlur={() => {
                        const clamped = clampMoneyToWire(value);
                        if (clamped !== value) setInput("selectedPremium", clamped);
                        commit("selectedPremium");
                        flash();
                    }}
                />
            </div>
        </div>
    );
}

/** The clickable "Suggested Premium" pill. */
function SuggestedPremiumChip({ amount, onClick }: { amount: number; onClick: () => void }) {
    return (
        <Tooltip title="Suggested Premium" description="Click to populate the input">
            <TooltipTrigger
                onClick={onClick}
                aria-label={`Use suggested premium ${displayMoneyValue(amount)}`}
                className="border-brand bg-brand-primary text-brand-secondary inline-flex cursor-pointer items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
            >
                <Lightbulb02 aria-hidden="true" className="size-3" />
                {displayMoneyValue(amount)}
            </TooltipTrigger>
        </Tooltip>
    );
}
